import React, { useState, useEffect } from 'react';
import Modal from './Modal';
import './AdminFormStyles.css';

interface GTMConfig {
  id?: number;
  container_id: string;
  container_name: string;
  is_active: boolean;
  environment: string;
  auth_token: string;
  preview_token: string;
  enable_enhanced_ecommerce: boolean;
}

interface GTMFormModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (data: GTMConfig) => Promise<void>;
  config?: GTMConfig | null;
}

const GTMFormModal: React.FC<GTMFormModalProps> = ({
  isOpen,
  onClose,
  onSave,
  config
}) => {
  const [formData, setFormData] = useState<GTMConfig>({
    container_id: '',
    container_name: '',
    is_active: true,
    environment: 'LIVE',
    auth_token: '',
    preview_token: '',
    enable_enhanced_ecommerce: true
  });
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState<any>({});

  useEffect(() => {
    if (config) {
      setFormData({
        id: config.id,
        container_id: config.container_id || '',
        container_name: config.container_name || '',
        is_active: config.is_active ?? true,
        environment: config.environment || 'LIVE',
        auth_token: config.auth_token || '',
        preview_token: config.preview_token || '',
        enable_enhanced_ecommerce: config.enable_enhanced_ecommerce ?? true
      });
    } else {
      setFormData({
        container_id: '',
        container_name: '',
        is_active: true,
        environment: 'LIVE',
        auth_token: '',
        preview_token: '',
        enable_enhanced_ecommerce: true
      });
    }
    setErrors({});
  }, [config, isOpen]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value, type } = e.target;
    const checked = (e.target as HTMLInputElement).checked;
    
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
    
    if (errors[name]) {
      setErrors((prev: any) => ({ ...prev, [name]: null }));
    }
  };

  const validate = () => {
    const newErrors: any = {};

    if (!formData.container_id.trim()) {
      newErrors.container_id = 'Container ID is required';
    } else if (!formData.container_id.match(/^GTM-[A-Z0-9]+$/i)) {
      newErrors.container_id = 'Invalid format. Use GTM-XXXXXXX';
    }

    if (formData.environment === 'CUSTOM') {
      if (!formData.auth_token.trim()) {
        newErrors.auth_token = 'Auth token is required for custom environment';
      }
      if (!formData.preview_token.trim()) {
        newErrors.preview_token = 'Preview token is required for custom environment';
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validate()) return;

    setLoading(true);
    try {
      await onSave(formData);
      onClose();
    } catch (error: any) {
      if (error.response?.data) {
        setErrors(error.response.data);
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={config ? 'Edit GTM Configuration' : 'Add GTM Configuration'}
      size="medium"
    >
      <form onSubmit={handleSubmit} className="admin-form-modal">
        <div className="admin-form-table">
          {/* Container ID */}
          <div className="admin-form-row">
            <div className="admin-form-label">Container ID *</div>
            <div className="admin-form-input">
              <input
                type="text"
                name="container_id"
                value={formData.container_id}
                onChange={handleChange}
                placeholder="GTM-XXXXXXX"
                className={errors.container_id ? 'has-error' : ''}
              />
              {errors.container_id && <span className="error-text">{errors.container_id}</span>}
            </div>
          </div>

          {/* Container Name */}
          <div className="admin-form-row">
            <div className="admin-form-label">Container Name</div>
            <div className="admin-form-input">
              <input
                type="text"
                name="container_name"
                value={formData.container_name}
                onChange={handleChange}
                placeholder="My Store GTM"
              />
            </div>
          </div>

          {/* Environment */}
          <div className="admin-form-row">
            <div className="admin-form-label">Environment</div>
            <div className="admin-form-input">
              <select
                name="environment"
                value={formData.environment}
                onChange={handleChange}
              >
                <option value="LIVE">Live/Production</option>
                <option value="LATEST">Latest (Testing)</option>
                <option value="CUSTOM">Custom Environment</option>
              </select>
            </div>
          </div>

          {/* Auth Token - only for CUSTOM environment */}
          {formData.environment === 'CUSTOM' && (
            <div className="admin-form-row">
              <div className="admin-form-label">Auth Token *</div>
              <div className="admin-form-input">
                <input
                  type="text"
                  name="auth_token"
                  value={formData.auth_token}
                  onChange={handleChange}
                  placeholder="gtm_auth parameter"
                  className={errors.auth_token ? 'has-error' : ''}
                />
                {errors.auth_token && <span className="error-text">{errors.auth_token}</span>}
              </div>
            </div>
          )}

          {/* Preview Token - only for CUSTOM environment */}
          {formData.environment === 'CUSTOM' && (
            <div className="admin-form-row">
              <div className="admin-form-label">Preview Token *</div>
              <div className="admin-form-input">
                <input
                  type="text"
                  name="preview_token"
                  value={formData.preview_token}
                  onChange={handleChange}
                  placeholder="gtm_preview parameter"
                  className={errors.preview_token ? 'has-error' : ''}
                />
                {errors.preview_token && <span className="error-text">{errors.preview_token}</span>}
              </div>
            </div>
          )}

          {/* Enhanced E-commerce */}
          <div className="admin-form-row checkbox-row">
            <div className="admin-form-label">Enhanced E-commerce</div>
            <div className="admin-form-input">
              <label className="admin-toggle-label">
                <input
                  type="checkbox"
                  name="enable_enhanced_ecommerce"
                  checked={formData.enable_enhanced_ecommerce}
                  onChange={handleChange}
                />
                <span>Enable GA4 Enhanced E-commerce tracking</span>
              </label>
            </div>
          </div>

          {/* Active Status */}
          <div className="admin-form-row checkbox-row">
            <div className="admin-form-label">Status</div>
            <div className="admin-form-input">
              <label className="admin-toggle-label">
                <input
                  type="checkbox"
                  name="is_active"
                  checked={formData.is_active}
                  onChange={handleChange}
                />
                <span>{formData.is_active ? 'Active' : 'Inactive'}</span>
              </label>
            </div>
          </div>
        </div>

        {/* Form Actions */}
        <div className="admin-form-actions">
          <button type="button" className="btn-cancel" onClick={onClose}>
            Cancel
          </button>
          <button type="submit" className="btn-save" disabled={loading}>
            {loading ? 'Saving...' : (config ? 'Update' : 'Create')}
          </button>
        </div>
      </form>
    </Modal>
  );
};

export default GTMFormModal;
